// Meal prep editorial — contrat public minimal et reproductible.
//
// Cette couche ne reçoit QUE le menu et les recettes déjà visibles dans
// l'application cliente. Elle ne lit ni anamnèse, ni analyses, ni contexte
// clinique. La proposition reste un brouillon jusqu'à validation explicite
// d'Anissa et devient automatiquement caduque si le menu change.

import { sha256HexSync } from "./sha256Sync";
import { resolveFourWeekRotationDays } from "./fourWeekRotation";
import { buildModularMealPrep } from "./modularMealPrep";

const MAX_SESSIONS = 3;
const MAX_TASKS_PER_SESSION = 8;
const MAX_TOTAL_TASKS = 24;
const MODULAR_DAYS = Object.freeze([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);
const MODULAR_DEFAULT_DAYS = Object.freeze({
  1: Object.freeze(["sunday"]),
  2: Object.freeze(["sunday", "wednesday"]),
  3: Object.freeze(["sunday", "tuesday", "thursday"]),
});
const MODULAR_OVERRIDE_KEYS = new Set([
  "version",
  "status",
  "source_revision",
  "people",
  "session_count",
  "preferred_days",
]);
const FORBIDDEN_PUBLIC_CONTENT = /\b(?:mgd|adn|génotype|genotype|anamnèse|anamnese|diagnostic|laboratoire|marqueur biologique|résultat sanguin|resultat sanguin|posologie)\b/i;

const OVERRIDE_KEYS = new Set([
  "version",
  "enabled",
  "status",
  "source_revision",
  "sessions",
]);
const SESSION_KEYS = new Set(["id", "title", "estimated_minutes", "tasks"]);
const TASK_KEYS = new Set([
  "id",
  "label",
  "detail",
  "estimated_minutes",
  "linked_meal_ids",
  "serves_days",
]);

function hasExactKeys(value, allowed) {
  return Object.keys(value).every((key) => allowed.has(key));
}

function cleanText(value, max) {
  if (typeof value !== "string") return null;
  const text = value.replace(/\s+/g, " ").trim();
  return text && text.length <= max ? text : null;
}

function cleanId(value) {
  const id = cleanText(value, 100);
  return id && /^[a-z0-9][a-z0-9:_-]*$/i.test(id) ? id : null;
}

function cleanMinutes(value) {
  if (value === undefined) return undefined;
  return Number.isInteger(value) && value >= 1 && value <= 240 ? value : null;
}

function cleanStringList(value, { maxItems, maxLength, ids = false }) {
  if (!Array.isArray(value) || value.length > maxItems) return null;
  const out = [];
  const seen = new Set();
  for (const item of value) {
    const cleaned = ids ? cleanId(item) : cleanText(item, maxLength);
    if (!cleaned) return null;
    if (!seen.has(cleaned)) {
      seen.add(cleaned);
      out.push(cleaned);
    }
  }
  return out;
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, stableValue(value[key])]),
  );
}

function normalizedMealSource(weekMeals) {
  const days = Array.isArray(weekMeals?.days) ? weekMeals.days : [];
  // Empreinte de la projection effectivement consommée/publiée. Ne jamais
  // réduire une valeur invalide à ""/null : deux contenus visibles distincts
  // doivent toujours produire deux révisions distinctes.
  return stableValue({
    structure: weekMeals?.structure || null,
    rotation: weekMeals?.rotation || null,
    days,
  });
}

export function buildMealPrepSourceRevision(weekMeals) {
  return `meal-prep-v2-${sha256HexSync(JSON.stringify(normalizedMealSource(weekMeals)))}`;
}

function normalizeTask(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (!hasExactKeys(value, TASK_KEYS)) return null;
  const id = cleanId(value.id);
  const label = cleanText(value.label, 180);
  const detail = value.detail === undefined ? undefined : cleanText(value.detail, 500);
  const estimatedMinutes = cleanMinutes(value.estimated_minutes);
  const linkedMealIds = cleanStringList(value.linked_meal_ids ?? [], {
    maxItems: 14,
    maxLength: 100,
    ids: true,
  });
  const servesDays = cleanStringList(value.serves_days ?? [], {
    maxItems: 7,
    maxLength: 60,
  });
  if (!id || !label || detail === null || estimatedMinutes === null || !linkedMealIds || !servesDays) {
    return null;
  }
  if (FORBIDDEN_PUBLIC_CONTENT.test(label) || (detail && FORBIDDEN_PUBLIC_CONTENT.test(detail))) {
    return null;
  }
  return {
    id,
    label,
    ...(detail ? { detail } : {}),
    ...(estimatedMinutes ? { estimated_minutes: estimatedMinutes } : {}),
    linked_meal_ids: linkedMealIds,
    serves_days: servesDays,
  };
}

function normalizeSession(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (!hasExactKeys(value, SESSION_KEYS)) return null;
  const id = cleanId(value.id);
  const title = cleanText(value.title, 120);
  const estimatedMinutes = cleanMinutes(value.estimated_minutes);
  if (!id || !title || estimatedMinutes === null || !Array.isArray(value.tasks)) return null;
  if (value.tasks.length > MAX_TASKS_PER_SESSION) return null;
  const tasks = value.tasks.map(normalizeTask);
  if (tasks.some((task) => !task)) return null;
  return {
    id,
    title,
    ...(estimatedMinutes ? { estimated_minutes: estimatedMinutes } : {}),
    tasks,
  };
}

/**
 * Valide le brouillon stocké dans editorial_overrides.meal_prep.
 * Toute clé enrichie ou incohérente invalide l'ensemble.
 */
export function parseMealPrepOverride(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (!hasExactKeys(value, OVERRIDE_KEYS)) return null;
  if (value.version !== 1 || typeof value.enabled !== "boolean") return null;
  if (value.status !== "draft" && value.status !== "validated") return null;
  const sourceRevision = cleanText(value.source_revision, 80);
  // Les anciennes empreintes 8 hex restent lisibles pour permettre leur
  // désactivation, mais ne peuvent plus être courantes face au SHA-256 neuf.
  if (!sourceRevision
      || !/^(?:meal-prep-v1-(?:[0-9a-f]{8}|[0-9a-f]{64})|meal-prep-v2-[0-9a-f]{64})$/.test(sourceRevision)) return null;
  if (!Array.isArray(value.sessions) || value.sessions.length > MAX_SESSIONS) return null;
  const sessions = value.sessions.map(normalizeSession);
  if (sessions.some((session) => !session)) return null;
  const totalTasks = sessions.reduce((sum, session) => sum + session.tasks.length, 0);
  if (totalTasks > MAX_TOTAL_TASKS) return null;
  const ids = new Set();
  for (const session of sessions) {
    if (ids.has(session.id)) return null;
    ids.add(session.id);
    for (const task of session.tasks) {
      if (ids.has(task.id)) return null;
      ids.add(task.id);
    }
  }
  return {
    version: 1,
    enabled: value.enabled,
    status: value.status,
    source_revision: sourceRevision,
    sessions,
  };
}

/** Valeur exacte à persister lorsqu'Anissa active/désactive le meal prep. */
export function buildMealPrepToggleValue(value, weekMeals) {
  const parsed = parseMealPrepOverride(value);
  const sourceRevision = buildMealPrepSourceRevision(weekMeals);
  if (parsed?.enabled) {
    return { ...parsed, enabled: false, status: "draft" };
  }
  return parsed
    ? { ...parsed, enabled: true, status: "draft", source_revision: sourceRevision }
    : {
        version: 1,
        enabled: true,
        status: "draft",
        source_revision: sourceRevision,
        sessions: [],
      };
}

function uniqueRecipeTasks(weekMeals) {
  const days = resolveFourWeekRotationDays(weekMeals);
  const byRecipe = new Map();
  for (const day of days) {
    const dayLabel = cleanText(day?.source_day_label || day?.label, 60);
    for (const meal of Array.isArray(day?.meals) ? day.meals : []) {
      const title = cleanText(meal?.title, 180);
      const slot = cleanId(meal?.slot);
      const mealId = cleanId(meal?.source_meal_id || meal?.id);
      const steps = Array.isArray(meal?.recipe?.preparation)
        ? meal.recipe.preparation.map((step) => cleanText(step, 300)).filter(Boolean)
        : [];
      if (!title || !slot || !mealId || steps.length === 0) continue;
      const ingredients = Array.isArray(meal?.recipe?.ingredients)
        ? meal.recipe.ingredients.map((item) => cleanText(item, 300)).filter(Boolean)
        : [];
      const prep = Number.isInteger(meal.recipe?.prep_time_min) ? meal.recipe.prep_time_min : 0;
      const cook = Number.isInteger(meal.recipe?.cook_time_min) ? meal.recipe.cook_time_min : 0;
      const key = JSON.stringify({
        slot,
        title: title.toLocaleLowerCase("fr"),
        ingredients,
        steps,
        prep,
        cook,
      });
      const existing = byRecipe.get(key) || {
        key,
        title,
        mealIds: [],
        dayLabels: [],
        steps,
        minutes: 0,
      };
      if (!existing.mealIds.includes(mealId)) existing.mealIds.push(mealId);
      if (dayLabel && !existing.dayLabels.includes(dayLabel)) existing.dayLabels.push(dayLabel);
      existing.minutes = Math.max(existing.minutes, prep + cook);
      byRecipe.set(key, existing);
    }
  }
  const tasks = Array.from(byRecipe.values());
  if (tasks.length > MAX_TOTAL_TASKS) {
    throw new Error("Le cycle contient trop de préparations distinctes. À valider Anissa.");
  }
  return tasks;
}

/** Crée une trame logistique à partir des seules recettes déjà approuvées. */
export function createMealPrepDraft(weekMeals) {
  const sourceRevision = buildMealPrepSourceRevision(weekMeals);
  const recipes = uniqueRecipeTasks(weekMeals);
  if (recipes.length === 0) {
    throw new Error("Génère d’abord les recettes détaillées de la semaine.");
  }
  const sessionCount = Math.min(MAX_SESSIONS, Math.ceil(recipes.length / MAX_TASKS_PER_SESSION));
  const chunkSize = Math.ceil(recipes.length / sessionCount);
  const sessions = [];
  for (let index = 0; index < sessionCount; index += 1) {
    const chunk = recipes.slice(index * chunkSize, (index + 1) * chunkSize);
    if (chunk.length === 0) continue;
    const tasks = chunk.map((recipe) => ({
      id: `prep-task-${sha256HexSync(recipe.key)}`,
      label: `Préparer ${recipe.title}`.slice(0, 180),
      detail: recipe.steps.slice(0, 3).join(" · ").slice(0, 500),
      ...(recipe.minutes > 0 && recipe.minutes <= 240
        ? { estimated_minutes: recipe.minutes }
        : {}),
      linked_meal_ids: recipe.mealIds.slice(0, 14),
      serves_days: recipe.dayLabels.slice(0, 7),
    }));
    const totalMinutes = tasks.reduce((sum, task) => sum + (task.estimated_minutes || 0), 0);
    sessions.push({
      id: `prep-session-${index + 1}`,
      title: sessionCount === 1
        ? "Ta session de préparation"
        : `Session ${index + 1} · ${index === 0 ? "Début de semaine" : "Suite de semaine"}`,
      ...(totalMinutes > 0 && totalMinutes <= 240 ? { estimated_minutes: totalMinutes } : {}),
      tasks,
    });
  }
  return {
    version: 1,
    enabled: true,
    status: "draft",
    source_revision: sourceRevision,
    sessions,
  };
}

/**
 * Raccord progressif du moteur v2. Le chemin v1 reste inchangé pour les
 * programmes dont les recettes ne sont pas encore assez structurées.
 */
export function createModularMealPrepDraft(weekMeals, options = {}) {
  const days = Array.isArray(weekMeals?.days) ? weekMeals.days : [];
  const meals = days.flatMap((day, dayIndex) => (
    Array.isArray(day?.meals) ? day.meals.map((meal) => ({
      id: meal?.source_meal_id || meal?.id,
      title: meal?.title,
      dayIndex: dayIndex + 1,
      dayLabel: day?.source_day_label || day?.label,
      recipe: meal?.recipe,
    })) : []
  ));
  const result = buildModularMealPrep({ meals, ...options });
  return {
    ...result,
    enabled: true,
    status: "draft",
    source_revision: buildMealPrepSourceRevision(weekMeals),
  };
}

function normalizeModularPreferences(value = {}) {
  const people = Number.isInteger(value.people) && value.people >= 1 && value.people <= 20
    ? value.people
    : 2;
  const sessionCount = Number.isInteger(value.session_count) && value.session_count >= 1 && value.session_count <= 3
    ? value.session_count
    : Number.isInteger(value.sessionCount) && value.sessionCount >= 1 && value.sessionCount <= 3
      ? value.sessionCount
      : 2;
  const requestedDays = Array.isArray(value.preferred_days)
    ? value.preferred_days
    : Array.isArray(value.preferredDays)
      ? value.preferredDays
      : [];
  const preferredDays = [];
  for (const day of requestedDays) {
    if (MODULAR_DAYS.includes(day) && !preferredDays.includes(day)) preferredDays.push(day);
  }
  for (const day of MODULAR_DEFAULT_DAYS[sessionCount]) {
    if (preferredDays.length >= sessionCount) break;
    if (!preferredDays.includes(day)) preferredDays.push(day);
  }
  return {
    people,
    sessionCount,
    preferredDays: preferredDays.slice(0, sessionCount),
  };
}

/** Contrat éditorial compact du Meal Prep v2. Les résultats dérivés ne sont jamais persistés. */
export function parseModularMealPrepOverride(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (!hasExactKeys(value, MODULAR_OVERRIDE_KEYS)) return null;
  if (value.version !== 2 || !["draft", "validated"].includes(value.status)) return null;
  if (typeof value.source_revision !== "string" || !/^meal-prep-v2-[a-f0-9]{64}$/.test(value.source_revision)) {
    return null;
  }
  if (!Number.isInteger(value.people) || value.people < 1 || value.people > 20) return null;
  if (!Number.isInteger(value.session_count) || value.session_count < 1 || value.session_count > 3) return null;
  if (!Array.isArray(value.preferred_days) || value.preferred_days.length !== value.session_count) return null;
  if (new Set(value.preferred_days).size !== value.preferred_days.length) return null;
  if (!value.preferred_days.every((day) => MODULAR_DAYS.includes(day))) return null;
  return {
    version: 2,
    status: value.status,
    source_revision: value.source_revision,
    people: value.people,
    session_count: value.session_count,
    preferred_days: [...value.preferred_days],
  };
}

export function buildModularMealPrepEditorialDraft(weekMeals, value = {}) {
  const parsed = parseModularMealPrepOverride(value);
  const preferences = normalizeModularPreferences(parsed || value);
  const result = createModularMealPrepDraft(weekMeals, {
    people: preferences.people,
    sessionCount: preferences.sessionCount,
    preferredDays: preferences.preferredDays,
  });
  const override = {
    version: 2,
    status: parsed?.status === "validated"
      && parsed.source_revision === result.source_revision
      ? "validated"
      : "draft",
    source_revision: result.source_revision,
    people: preferences.people,
    session_count: preferences.sessionCount,
    preferred_days: [...result.preferredDays || preferences.preferredDays],
  };
  return { override, result };
}

export function updateModularMealPrepPreferences(weekMeals, current, patch = {}) {
  const base = parseModularMealPrepOverride(current) || buildModularMealPrepEditorialDraft(weekMeals, current).override;
  return buildModularMealPrepEditorialDraft(weekMeals, {
    ...base,
    ...patch,
    status: "draft",
  });
}

function publicModularResult(result) {
  if (!result?.ready || !Array.isArray(result.sessions) || !Array.isArray(result.shoppingList)) {
    return null;
  }
  return {
    sessions: result.sessions.map((session) => ({
      id: session.id,
      preferred_day: session.preferredDay,
      meal_days: [...session.mealDays],
      components: session.components.map((component) => ({
        id: component.componentId,
        label: component.label,
        quantity: component.quantity,
        unit: component.unit,
        ...(component.preparation ? { preparation: component.preparation } : {}),
        usages: component.usages.map((usage) => ({
          meal_id: usage.mealId,
          meal_title: usage.mealTitle,
          day: usage.day,
        })),
      })),
    })),
    shopping_list: result.shoppingList.map((item) => ({
      food_key: item.foodKey,
      label: item.label,
      category: item.category,
      quantity: item.quantity,
      unit: item.unit,
    })),
  };
}

/** DTO public v2 : résultat calculé côté SaaS, sans état éditorial ni empreinte interne. */
export function buildPublishedModularMealPrep(weekMeals, value) {
  const parsed = parseModularMealPrepOverride(value);
  if (!parsed || parsed.status !== "validated") return null;
  if (parsed.source_revision !== buildMealPrepSourceRevision(weekMeals)) return null;
  const { result } = buildModularMealPrepEditorialDraft(weekMeals, parsed);
  const base = publicModularResult(result);
  if (!base || base.sessions.length !== parsed.session_count) return null;

  let rotation;
  if (weekMeals?.rotation) {
    let expandedDays;
    try {
      expandedDays = resolveFourWeekRotationDays(weekMeals);
    } catch {
      return null;
    }
    if (!Array.isArray(expandedDays) || expandedDays.length !== 28) return null;
    const weeks = Array.from({ length: 4 }, (_, index) => {
      const days = expandedDays.slice(index * 7, (index + 1) * 7);
      const weekResult = createModularMealPrepDraft({ days }, {
        people: parsed.people,
        sessionCount: parsed.session_count,
        preferredDays: parsed.preferred_days,
      });
      const publicWeek = publicModularResult(weekResult);
      if (!publicWeek || publicWeek.sessions.length !== parsed.session_count) return null;
      return {
        id: `week-${index + 1}`,
        index: index + 1,
        label: `Semaine ${index + 1}`,
        ...publicWeek,
      };
    });
    if (weeks.some((week) => !week)) return null;
    rotation = { version: 1, weeks };
  }

  const current = rotation?.weeks?.[0] || base;
  return {
    version: 2,
    people: parsed.people,
    session_count: parsed.session_count,
    preferred_days: [...parsed.preferred_days],
    sessions: current.sessions,
    shopping_list: current.shopping_list,
    ...(rotation ? { rotation } : {}),
  };
}

/**
 * Raison concrète pour laquelle buildPublishedModularMealPrep refuserait ce
 * Meal Prep (null si tout passe). Sert à dire à Anissa QUOI corriger.
 */
export function explainPublishedModularMealPrepIssue(weekMeals, value) {
  const parsed = parseModularMealPrepOverride(value);
  if (!parsed) return "réglages du Meal Prep illisibles";
  if (parsed.status !== "validated") return "Meal Prep pas encore validé";
  if (parsed.source_revision !== buildMealPrepSourceRevision(weekMeals)) {
    return "les menus ont changé depuis le calcul du Meal Prep";
  }
  const options = {
    people: parsed.people,
    sessionCount: parsed.session_count,
    preferredDays: parsed.preferred_days,
  };
  const describe = (result, label) => {
    if (!result?.ready) return `${label} : ${result?.issues?.[0] || "les recettes ne permettent pas de calculer les préparations"}`;
    if (!Array.isArray(result.sessions) || result.sessions.length !== parsed.session_count) {
      return `${label} : ${result?.sessions?.length || 0} session(s) calculée(s) au lieu de ${parsed.session_count} (pas assez de repas à préparer à l’avance ces jours-là ; baisse le nombre de sessions)`;
    }
    return null;
  };
  const baseIssue = describe(buildModularMealPrepEditorialDraft(weekMeals, parsed).result, "Semaine type");
  if (baseIssue) return baseIssue;
  if (weekMeals?.rotation) {
    let expandedDays;
    try {
      expandedDays = resolveFourWeekRotationDays(weekMeals);
    } catch (caught) {
      return `rotation 4 semaines : ${caught?.message || "illisible"}`;
    }
    if (!Array.isArray(expandedDays) || expandedDays.length !== 28) return "rotation 4 semaines incomplète";
    for (let index = 0; index < 4; index += 1) {
      const issue = describe(
        createModularMealPrepDraft({ days: expandedDays.slice(index * 7, (index + 1) * 7) }, options),
        `Semaine ${index + 1} de la rotation`,
      );
      if (issue) return issue;
    }
  }
  return null;
}

export function getMealPrepPublicationIssue(weekMeals, value) {
  if (!value) return null;
  const parsed = parseMealPrepOverride(value);
  if (!parsed) return "Meal prep à vérifier";
  if (!parsed.enabled) return null;
  if (parsed.source_revision !== buildMealPrepSourceRevision(weekMeals)) {
    return "Meal prep à actualiser après les modifications du menu";
  }
  const totalTasks = parsed.sessions.reduce((sum, session) => sum + session.tasks.length, 0);
  if (parsed.status !== "validated" || totalTasks === 0) {
    return "Meal prep activé mais pas encore validé";
  }
  return null;
}

/** DTO publié : aucune information d'édition ou signature technique. */
export function buildPublishedMealPrep(weekMeals, value) {
  if (getMealPrepPublicationIssue(weekMeals, value)) return null;
  const parsed = parseMealPrepOverride(value);
  if (!parsed?.enabled) return null;
  return {
    version: 1,
    title: "Préparer ta semaine",
    intro: "Des gestes regroupés pour avancer simplement, à ton rythme.",
    sessions: parsed.sessions,
  };
}
